import React, {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Button, Descriptions, Divider, Popconfirm, Progress, Select, Space, Table, message} from "antd";
import {CheckOutlined, LoadingOutlined, ReloadOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import api from "../api";
import {handleApiError} from "../helpers/formatApiError";
import useConsumer from "../../channels/consumer";
import {standardMetadataFieldLabel, standardMetadataFieldsForKeys} from "./standardFields";

export function metadataEntityTypeFields(metadataEntityType) {
    const fields = [];
    const ids = new Set();
    const addField = field => {
        if (!field || ids.has(field.id)) return;
        ids.add(field.id);
        fields.push(field);
    };

    metadataEntityType?.custom_meta_groups?.forEach(groupMembership => {
        groupMembership.custom_meta_group?.custom_meta_fields?.forEach(fieldMembership => {
            addField(fieldMembership.custom_meta_field);
        });
    });

    metadataEntityType?.custom_meta_fields?.forEach(fieldMembership => {
        addField(fieldMembership.custom_meta_field);
    });

    return fields;
}

export function metadataEntityMappingValue(column) {
    const mapping = column.mapping;
    if (!mapping) return "raw";

    if (mapping.type === "custom_meta_field") return `custom_meta_field:${mapping.custom_meta_field_id}`;
    if (mapping.type === "standard_metadata_field") return `standard_metadata_field:${mapping.standard_metadata_field}`;
    if (mapping.type === "tag_field") return `tag_field:${mapping.tag_field}`;
    return mapping.type;
}

export function metadataEntityMappingOptions(metadataEntityType, t) {
    const fields = metadataEntityTypeFields(metadataEntityType);
    const standardFields = standardMetadataFieldsForKeys(metadataEntityType?.standard_field_keys);
    const tagFields = metadataEntityType?.tag_fields || [];

    return [
        {value: "source", label: t("file-handling-only", "File Handling only")},
        {value: "ignored", label: t("ignore", "Ignore")},
        {value: "raw", label: t("ignore-source-field", "Ignore source field")},
        ...tagFields.map(field => ({
            value: field.key === "taxonomy_path" ? "taxonomy_path" : `tag_field:${field.key}`,
            label: `${t("standard-tag-field", "Standard tag field")}: ${t(field.key, field.label)}`
        })),
        ...standardFields.map(field => ({
            value: `standard_metadata_field:${field.key}`,
            label: `${t("standard-metadata-field", "Standard metadata field")}: ${t(field.key, field.label)}`
        })),
        ...fields.map(field => ({
            value: `custom_meta_field:${field.id}`,
            label: `${t("structured-field", "Structured field")}: ${field.name}${field.owner_type ? ` (${field.owner_type})` : ""}`
        }))
    ];
}

export function metadataEntitySelectOptions(metadataEntityType, t, csvConfiguration) {
    return metadataEntityMappingOptions(metadataEntityType, t).filter(option => csvConfiguration || option.value !== "source");
}

export function decodeMetadataEntityMappingValue(metadataEntityType, value) {
    if (value === "raw") return null;
    if (["ignored", "source"].includes(value)) return {type: value};
    if (value === "taxonomy_path") return {type: "taxonomy_path"};

    const [type, id] = value.split(":");
    if (type === "tag_field") return {type, tag_field: id};
    if (type === "standard_metadata_field") {
        return {
            type,
            standard_metadata_field: id,
            standard_metadata_field_label: standardMetadataFieldLabel(id)
        };
    }
    if (type === "custom_meta_field") {
        const field = metadataEntityTypeFields(metadataEntityType).find(field => field.id === parseInt(id, 10));
        return {
            type,
            custom_meta_field_id: parseInt(id, 10),
            custom_meta_field_name: field?.name
        };
    }

    return null;
}

export function metadataEntityMappingLabel(metadataEntityType, column, t) {
    const value = metadataEntityMappingValue(column);
    return metadataEntityMappingOptions(metadataEntityType, t).find(option => option.value === value)?.label;
}

export default function MetadataEntityImportSetup({metadataImport, onDone}) {
    const {t} = useTranslation();
    const [attrs, setAttrs] = useState(metadataImport);
    const [progress, setProgress] = useState(0);
    const [starting, setStarting] = useState(false);
    const [refreshingPreview, setRefreshingPreview] = useState(false);
    const [reconcilingPreview, setReconcilingPreview] = useState(false);
    const [previewUnconfirmed, setPreviewUnconfirmed] = useState(false);
    const [retryPollingExpired, setRetryPollingExpired] = useState(false);
    const [unavailableStatus, setUnavailableStatus] = useState(null);
    const startingRef = useRef(false);
    const refreshingPreviewRef = useRef(false);
    const reconcilingPreviewRef = useRef(false);
    const previewUnconfirmedRef = useRef(false);
    const previewBaselineRef = useRef(null);
    const reconciliationDefinitiveRef = useRef(false);
    const reconciliationPollsRemainingRef = useRef(10);
    const retryPollsRemainingRef = useRef(30);
    const retryPollKeyRef = useRef(null);
    const retryingPreviewRef = useRef(false);
    const unavailableStatusRef = useRef(null);
    const rowErrors = Array.isArray(attrs.processing_error?.row_errors) ? attrs.processing_error.row_errors : [];
    const identityColumn = (attrs.columns || []).find(column => column.name === attrs.sources?.tag_name);
    const attachmentPreview = attrs.attachment_preview || {};
    const previewStatus = attachmentPreview.refresh_status;
    const previewRecoverable = previewStatus === 'pending' && attachmentPreview.refresh_recoverable === true;
    const retryingPreview = previewStatus === 'failed' && attachmentPreview.refresh_retryable && !retryPollingExpired;
    retryingPreviewRef.current = retryingPreview;
    const previousPreview = refreshingPreview || reconcilingPreview || previewUnconfirmed || ['pending', 'failed', 'stale'].includes(previewStatus);

    const consumer = useConsumer();
    const subscription = useRef();
    const activeRun = useRef(null);
    const completedRun = useRef(null);
    const refreshEpoch = useRef(0);
    const refreshSequence = useRef(0);
    const appliedRefreshSequence = useRef(0);
    const currentImportId = useRef(metadataImport.id);
    const mounted = useRef(true);
    currentImportId.current = metadataImport.id;

    const refreshImport = useCallback((importId, isActive = () => true) => {
        if (unavailableStatusRef.current) return Promise.resolve();
        const epoch = refreshEpoch.current;
        const sequence = ++refreshSequence.current;
        return api.get(`/api/metadata_entity_imports/${importId}`).then(res => {
            if (!isActive() || !mounted.current || currentImportId.current !== importId ||
                startingRef.current || unavailableStatusRef.current || epoch !== refreshEpoch.current || sequence < appliedRefreshSequence.current) return;
            appliedRefreshSequence.current = sequence;
            setAttrs(prev => ({...prev, ...res.data}));
            if (reconcilingPreviewRef.current || previewUnconfirmedRef.current) {
                const baseline = previewBaselineRef.current;
                const preview = res.data.attachment_preview || {};
                const changed = !baseline || res.data.aasm_state !== baseline.aasm_state ||
                    preview.refresh_key !== baseline.refresh_key ||
                    preview.refresh_status !== baseline.refresh_status ||
                    preview.checked_at !== baseline.checked_at;
                if (changed) {
                    reconcilingPreviewRef.current = false;
                    setReconcilingPreview(false);
                    previewUnconfirmedRef.current = false;
                    setPreviewUnconfirmed(false);
                } else if (reconciliationDefinitiveRef.current) {
                    reconcilingPreviewRef.current = false;
                    setReconcilingPreview(false);
                    previewUnconfirmedRef.current = true;
                    setPreviewUnconfirmed(true);
                }
            }
        }).catch(error => {
            if (!isActive() || !mounted.current || currentImportId.current !== importId ||
                startingRef.current || unavailableStatusRef.current || epoch !== refreshEpoch.current || sequence < appliedRefreshSequence.current) return;
            const status = error?.response?.status;
            if (status === 403 || status === 404) {
                const wasReconciling = reconcilingPreviewRef.current;
                appliedRefreshSequence.current = sequence;
                unavailableStatusRef.current = status;
                setUnavailableStatus(status);
                reconcilingPreviewRef.current = false;
                setReconcilingPreview(false);
                previewUnconfirmedRef.current = false;
                setPreviewUnconfirmed(false);
                if (!wasReconciling) handleApiError(error);
            } else if (!reconcilingPreviewRef.current && !retryingPreviewRef.current) {
                handleApiError(error);
            }
        });
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        refreshEpoch.current += 1;
        setAttrs(metadataImport);
        setProgress(0);
        setStarting(false);
        setRefreshingPreview(false);
        setReconcilingPreview(false);
        setPreviewUnconfirmed(false);
        setRetryPollingExpired(false);
        setUnavailableStatus(null);
        startingRef.current = false;
        refreshingPreviewRef.current = false;
        reconcilingPreviewRef.current = false;
        previewUnconfirmedRef.current = false;
        previewBaselineRef.current = null;
        reconciliationDefinitiveRef.current = false;
        retryPollKeyRef.current = null;
        retryPollsRemainingRef.current = 30;
        unavailableStatusRef.current = null;
        activeRun.current = metadataImport.aasm_state === 'processing' ? metadataImport.started_processing_at : null;
        completedRun.current = null;
    }, [metadataImport.id]);

    useEffect(() => {
        let active = true;
        refreshImport(metadataImport.id, () => active);

        if (consumer && !subscription.current) {
            subscription.current = consumer.subscriptions.create({channel: "UserChannel"}, {
                received: data => {
                    switch (data.type) {
                        case "metadataEntityImport":
                            // Broadcasts carry model attributes, while readiness and
                            // schema details come from the API's serialized view.
                            if (data.attrs?.id === metadataImport.id) {
                                refreshImport(metadataImport.id, () => active);
                            }
                            return;

                        case "metadataEntityImportProgress":
                            if (data.id === metadataImport.id) {
                                if (!startingRef.current && typeof data.progress === "number") setProgress(data.progress);
                                if (data.done) refreshImport(metadataImport.id, () => active);
                            }
                            return;
                    }
                }
            });
        }

        return () => {
            active = false;
            subscription.current?.unsubscribe();
            subscription.current = null;
        };
    }, [metadataImport.id, consumer, refreshImport]);

    useEffect(() => {
        if (previewStatus === 'failed' && attachmentPreview.refresh_retryable) {
            if (retryPollKeyRef.current !== attachmentPreview.refresh_key) {
                retryPollKeyRef.current = attachmentPreview.refresh_key;
                retryPollsRemainingRef.current = 30;
                setRetryPollingExpired(false);
            }
        } else {
            retryPollKeyRef.current = null;
            retryPollsRemainingRef.current = 30;
            setRetryPollingExpired(false);
        }
    }, [metadataImport.id, previewStatus, attachmentPreview.refresh_key, attachmentPreview.refresh_retryable]);

    useEffect(() => {
        if (unavailableStatus || (!['pending', 'analyzing', 'processing'].includes(attrs.aasm_state) &&
            previewStatus !== 'pending' && !reconcilingPreview && !retryingPreview)) return;
        let active = true;
        const interval = window.setInterval(() => {
            if (reconcilingPreviewRef.current && --reconciliationPollsRemainingRef.current <= 0) {
                reconcilingPreviewRef.current = false;
                setReconcilingPreview(false);
                previewUnconfirmedRef.current = true;
                setPreviewUnconfirmed(true);
            }
            if (retryingPreviewRef.current && --retryPollsRemainingRef.current <= 0) {
                retryingPreviewRef.current = false;
                setRetryPollingExpired(true);
            }
            refreshImport(metadataImport.id, () => active);
        }, 2000);
        return () => { active = false; window.clearInterval(interval); };
    }, [attrs.aasm_state, previewStatus, reconcilingPreview, retryingPreview, unavailableStatus, metadataImport.id, refreshImport]);

    useEffect(() => {
        if (attrs.id !== metadataImport.id) return;
        if (attrs.aasm_state === 'processing') {
            if (attrs.started_processing_at) activeRun.current = attrs.started_processing_at;
            startingRef.current = false;
            return;
        }
        if (attrs.aasm_state !== 'processed' || !activeRun.current ||
            attrs.started_processing_at !== activeRun.current || completedRun.current === activeRun.current) return;
        completedRun.current = activeRun.current;
        message.success(t('message-processing-done', 'Processing Done!'));
        onDone?.();
    }, [attrs.id, attrs.aasm_state, attrs.started_processing_at, metadataImport.id, onDone, t]);

    const updateColumnMapping = (column, value) => {
        if (unavailableStatusRef.current) return;
        api.put(`/api/metadata_entity_imports/${metadataImport.id}/mapping`, {
            column: {
                ...column,
                mapping: decodeMetadataEntityMappingValue(attrs.metadata_entity_type, value)
            }
        }).then(res => {
            message.success(t("column-mapping-updated", "Column mapping updated."));
            setAttrs(prev => ({...prev, ...res.data}));
        }).catch(handleApiError);
    };

    const startProcessing = () => {
        const canStart = ['analyzed', 'processed'].includes(attrs.aasm_state);
        if (startingRef.current || refreshingPreviewRef.current || unavailableStatusRef.current || !canStart || attrs.ready_for_processing !== true) return;
        startingRef.current = true;
        const requestEpoch = ++refreshEpoch.current;
        activeRun.current = null;
        const importId = metadataImport.id;
        setStarting(true);
        setProgress(0);
        api.post(`/api/metadata_entity_imports/${importId}/start_process`).then(res => {
            if (!mounted.current || currentImportId.current !== importId || requestEpoch !== refreshEpoch.current) return;
            refreshEpoch.current += 1;
            activeRun.current = res.data.started_processing_at;
            completedRun.current = null;
            setStarting(false);
            setAttrs(prev => ({...prev, ...res.data}));
            reconcilingPreviewRef.current = false;
            setReconcilingPreview(false);
        }).catch(error => {
            if (!mounted.current || currentImportId.current !== importId || requestEpoch !== refreshEpoch.current) return;
            refreshEpoch.current += 1;
            setStarting(false);
            startingRef.current = false;
            handleApiError(error);
        });
    };

    const refreshFileCheck = () => {
        if (startingRef.current || refreshingPreviewRef.current || reconcilingPreviewRef.current ||
            retryingPreviewRef.current || unavailableStatusRef.current ||
            (attrs.attachment_preview?.refresh_status === 'pending' && attrs.attachment_preview?.refresh_recoverable !== true) ||
            !['analyzed', 'processed'].includes(attrs.aasm_state)) return;
        const requestEpoch = ++refreshEpoch.current;
        const importId = metadataImport.id;
        previewBaselineRef.current = {
            aasm_state: attrs.aasm_state,
            refresh_key: attachmentPreview.refresh_key,
            refresh_status: attachmentPreview.refresh_status,
            checked_at: attachmentPreview.checked_at
        };
        previewUnconfirmedRef.current = false;
        setPreviewUnconfirmed(false);
        refreshingPreviewRef.current = true;
        setRefreshingPreview(true);
        api.post(`/api/metadata_entity_imports/${importId}/refresh_attachment_preview`).then(res => {
            if (!mounted.current || currentImportId.current !== importId || requestEpoch !== refreshEpoch.current) return;
            // Any GET started before the accepted refresh may still contain the old preview.
            appliedRefreshSequence.current = ++refreshSequence.current;
            setAttrs(prev => ({...prev, ...res.data}));
            reconcilingPreviewRef.current = false;
            setReconcilingPreview(false);
            previewUnconfirmedRef.current = false;
            setPreviewUnconfirmed(false);
        }).catch(error => {
            if (!mounted.current || currentImportId.current !== importId || requestEpoch !== refreshEpoch.current) return;
            handleApiError(error);
            const status = error?.response?.status;
            if (status === 403 || status === 404) {
                appliedRefreshSequence.current = ++refreshSequence.current;
                unavailableStatusRef.current = status;
                setUnavailableStatus(status);
                return;
            }
            // The POST may have failed before or after enqueue. Read the server's
            // status and prevent an older GET from restoring the last completed check.
            appliedRefreshSequence.current = ++refreshSequence.current;
            reconcilingPreviewRef.current = true;
            setReconcilingPreview(true);
            reconciliationDefinitiveRef.current = status === 422;
            reconciliationPollsRemainingRef.current = 10;
            refreshImport(importId);
        }).finally(() => {
            if (mounted.current && currentImportId.current === importId && requestEpoch === refreshEpoch.current) {
                refreshingPreviewRef.current = false;
                setRefreshingPreview(false);
            }
        });
    };

    if (unavailableStatus) {
        return <Alert
            type="error"
            showIcon
            message={unavailableStatus === 404
                ? t('file-check-import-missing', 'This import is no longer available.')
                : t('file-check-import-forbidden', 'You cannot access this import now.')}
            description={<Space direction="vertical">
                <span>{t('file-check-import-unavailable-help', 'Close this dialog. The retained file-check results below may be out of date.')}</span>
                {attachmentPreview.checked_at && <>
                    <span>{t('previous-file-check', 'Previous file check')}: {attachmentPreview.counts?.eligible || 0} {t('eligible', 'eligible')}, {attachmentPreview.counts?.missing || 0} {t('missing', 'missing')}, {attachmentPreview.counts?.ambiguous || 0} {t('ambiguous', 'ambiguous')}, {attachmentPreview.counts?.forbidden || 0} {t('inaccessible', 'inaccessible')}, {attachmentPreview.counts?.blank || 0} {t('blank', 'blank')}.</span>
                    <span>{t('previously-checked-at', 'Previously checked at')}: {new Date(attachmentPreview.checked_at).toLocaleString()}</span>
                </>}
                {(attachmentPreview.warnings || []).map((warning, index) => <span key={index}>{t('previous-file-check', 'Previous file check')}: {warning.filename}: {warning.kind.replaceAll('_', ' ')}</span>)}
            </Space>}
        />;
    }

    if (attrs.aasm_state === "processing") {
        return (
            <>
                <LoadingOutlined/> {t("processing-progress", "Processing progress")}:
                <Progress percent={progress || 0} status={"active"}/>
            </>
        );
    }

    if (attrs.aasm_state === "errored") {
        const error = attrs.parse_error || attrs.processing_error;
        return (
            <Alert
                type={"error"}
                showIcon
                message={attrs.parse_error ? t("parse-error", "Parse Error") : t("processing-error", "Processing Error")}
                description={`${t("import-error-description", "Unable to import file, please try another.")}${error?.message ? ` (${error.message})` : ""}`}
            />
        );
    }

    return (
        <>
            {(attrs.aasm_state === "analyzing" || attrs.aasm_state === "pending") && (
                <Alert
                    style={{marginBottom: "2em"}}
                    type={"info"}
                    showIcon
                    message={<em><LoadingOutlined/> {t("analyzing", "Analyzing")}...</em>}
                />
            )}
            {attrs.aasm_state === 'analyzed' && attrs.csv_configuration && <Alert style={{marginBottom: 16}} type="info" showIcon message={t('import-ready-for-review', 'Analysis complete. Review the mappings, then choose Process Import.')} description={t('import-preview-guard', 'This import reserves its schema until it is processed or deleted from Imports. Closing this window will not process it.')}/>}

            <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label={t("filename", "Filename")}>{attrs.filename}</Descriptions.Item>
                <Descriptions.Item label={t("custom-metadata-entity-type", "Custom Metadata Entity Type")}>{attrs.metadata_entity_type?.name}</Descriptions.Item>
                <Descriptions.Item label={t("rows", "Rows")}>{attrs.rows}</Descriptions.Item>
                <Descriptions.Item label={t("matching-existing-entities", "Matching existing entities")}>{attrs.matches}</Descriptions.Item>
            </Descriptions>

            {attrs.csv_configuration && attrs.sources?.attach_to_files && ['analyzed', 'processed'].includes(attrs.aasm_state) && <Alert
                style={{marginTop: 16}}
                type={refreshingPreview || reconcilingPreview || previewUnconfirmed || retryingPreview ? 'warning' : previewStatus === 'failed' ? 'error' : previewStatus === 'stale' ? 'warning' :
                    previewStatus === 'pending' || attachmentPreview.counts?.eligible ? 'info' : 'warning'}
                showIcon
                message={refreshingPreview
                    ? t('file-check-requesting', 'Requesting a file check. Previous results may be out of date.')
                    : reconcilingPreview
                    ? t('file-check-status-unconfirmed', 'File check status is being confirmed. Previous results may be out of date.')
                    : previewUnconfirmed
                    ? t('file-check-status-still-unconfirmed', 'File check status could not be confirmed. Previous results may be out of date; refresh to try again.')
                    : retryingPreview
                    ? t('file-check-retrying', 'File check was interrupted and may retry automatically. Previous results may be out of date.')
                    : previewStatus === 'failed' && attachmentPreview.refresh_retryable && retryPollingExpired
                    ? t('file-check-retry-unconfirmed', 'The file check may still retry in the background. Previous results may be out of date; reopen this import to check or refresh to try again.')
                    : previewStatus === 'failed'
                    ? t('file-check-failed', 'File check failed. Refresh to try again; previous results may be out of date.')
                    : previewStatus === 'stale'
                        ? t('file-check-stale', 'File check became outdated while the import changed. Refresh to check again.')
                        : previewRecoverable
                            ? t('file-check-enqueue-unconfirmed', 'The file check may not have started. Refresh to retry the queued check.')
                        : previewStatus === 'pending'
                            ? t('file-check-pending', 'Checking files now. Previous results may be out of date.')
                            : attachmentPreview.checked_at
                                ? `${attrs.rows || 0} ${t('entities-ready', 'entity rows ready')}; ${attachmentPreview.counts?.eligible || 0} ${t('files-currently-eligible', 'files currently eligible for attachment')}`
                                : t('file-check-not-run', 'File check has not run for this import. Refresh to check current filenames and permissions.')}
                description={<Space direction="vertical">
                    <span>{t('exact-filename-check', 'Files match exact filenames in this organization. Missing files and filenames matching multiple files you can tag will not attach. File checks are advisory; processing checks again.')}</span>
                    {attachmentPreview.checked_at && <>
                        <span>{previousPreview ? t('previous-file-check', 'Previous file check') : t('file-check-outcomes', 'File checks')}: {attachmentPreview.counts?.eligible || 0} {t('eligible', 'eligible')}, {attachmentPreview.counts?.missing || 0} {t('missing', 'missing')}, {attachmentPreview.counts?.ambiguous || 0} {t('ambiguous', 'ambiguous')}, {attachmentPreview.counts?.forbidden || 0} {t('inaccessible', 'inaccessible')}, {attachmentPreview.counts?.blank || 0} {t('blank', 'blank')}.</span>
                        <span>{previousPreview ? t('previously-checked-at', 'Previously checked at') : t('checked-at', 'Checked at')}: {new Date(attachmentPreview.checked_at).toLocaleString()}</span>
                    </>}
                    <Button icon={<ReloadOutlined/>} loading={refreshingPreview || reconcilingPreview || retryingPreview || (previewStatus === 'pending' && !previewRecoverable)} disabled={starting} onClick={refreshFileCheck}>{t('refresh-file-check', 'Refresh file check')}</Button>
                </Space>}
            />}

            <Divider/>

            {attrs.csv_configuration && attrs.sources?.tag_name && <Descriptions bordered size="small" column={1} style={{marginBottom: 16}}>
                <Descriptions.Item label={t('tag-name', 'Tag Name')}>{attrs.sources.tag_name}{identityColumn && <> · {t('first-row', 'First Row')}: <span>{identityColumn.sample ?? ''}</span></>}{identityColumn?.mapping?.type && identityColumn.mapping.type !== 'source' && ` · ${metadataEntityMappingLabel(attrs.metadata_entity_type, identityColumn, t) || identityColumn.mapping.type}`}</Descriptions.Item>
            </Descriptions>}
            {(attrs.processing_requirement_errors || []).map((error, index) => <Alert key={index} type="error" showIcon message={error} style={{marginBottom: 8}}/>)}

            <Table
                rowKey={column => `${attrs.id}-${column.index}-${column.name}`}
                size="small"
                dataSource={(attrs.columns || []).filter(column => !attrs.csv_configuration || column.name !== attrs.sources?.tag_name)}
                pagination={false}
                columns={[
                    {
                        title: t("source-column", "Source Column"),
                        dataIndex: "name"
                    },
                    {title: t("first-row", "First Row"), dataIndex: "sample", render: value => value ?? ""},
                    {
                        title: t("mapping", "Mapping"),
                        render: (_, column) => (
                            <Select
                                style={{width: "100%"}}
                                value={metadataEntityMappingValue(column)}
                                options={metadataEntitySelectOptions(attrs.metadata_entity_type, t, attrs.csv_configuration)}
                                onChange={value => updateColumnMapping(column, value)}
                                optionFilterProp="label"
                                showSearch
                                disabled={attrs.csv_configuration || ["processing", "processed"].includes(attrs.aasm_state)}
                            />
                        )
                    }
                ]}
            />

            {attrs.aasm_state === "processed" && <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label={t('historical-import-results', 'Historical import results')}>{t('actual-outcomes-from-last-run', 'Actual outcomes from the last processing run')}</Descriptions.Item>
                <Descriptions.Item label={t("created", "Created")}>{attrs.created_count}</Descriptions.Item>
                <Descriptions.Item label={t("updated", "Updated")}>{attrs.updated_count}</Descriptions.Item>
                <Descriptions.Item label={t("skipped", "Skipped")}>{attrs.skipped_count}</Descriptions.Item>
                {attrs.sources?.attach_to_files && <Descriptions.Item label={t('files-attached', 'Files attached')}>{attrs.attachment_results?.attached || 0}</Descriptions.Item>}
                {Object.entries(attrs.attachment_results || {}).filter(([key]) => !['warnings', 'attached'].includes(key)).map(([key, value]) => <Descriptions.Item key={key} label={key.replaceAll('_', ' ')}>{value}</Descriptions.Item>)}
            </Descriptions>}
            {attrs.aasm_state === "processed" && rowErrors.map((rowError, index) => (
                <Alert
                    key={`${rowError.name || rowError.row || "row"}-${index}`}
                    type="warning"
                    showIcon
                    message={rowError.name || (rowError.row ? `${t("row", "Row")} ${rowError.row}` : t("skipped-row", "Skipped row"))}
                    description={Array.isArray(rowError.errors) ? rowError.errors.join("; ") : rowError.message}
                />
            ))}
            {(attachmentPreview.warnings || []).map((warning, index) => <Alert key={`preview-${index}`} type="warning" message={`${previousPreview ? t('previous-file-check', 'Previous file check') : t('current-file-check', 'Current file check')}: ${warning.filename}: ${warning.kind.replaceAll('_', ' ')}`}/>)}
            {(attrs.attachment_results?.warnings || []).map((warning, index) => <Alert key={`actual-${index}`} type="warning" message={`${t('last-processing-run', 'Last processing run')}: ${warning.filename}: ${warning.kind.replaceAll('_', ' ')}`}/>)}

            <Popconfirm title={attrs.aasm_state === 'processed'
                ? t('confirm-reprocess-import', 'Reprocess the original uploaded file? Matching records may be updated.')
                : t("confirm-process-import", "Process import?")} onConfirm={startProcessing}>
                <Button
                    icon={<CheckOutlined/>}
                    type={"primary"}
                    block
                    style={{marginTop: "2em"}}
                    loading={starting}
                    disabled={starting || refreshingPreview || attrs.ready_for_processing !== true ||
                        !['analyzed', 'processed'].includes(attrs.aasm_state)}
                >
                    {attrs.aasm_state === 'processed'
                        ? t('button-reprocess-import', 'Reprocess Import')
                        : attrs.sources?.attach_to_files && attrs.attachment_preview?.checked_at && !attrs.attachment_preview?.counts?.eligible
                            ? t('import-entities-anyway', 'Import entities anyway')
                            : t("button-process-import", "Process Import")}
                </Button>
            </Popconfirm>
        </>
    );
}
